import * as Diff2Html from "diff2html";
import { Panel } from "./panel";
import type { TestCaseEvent } from "../telemetry/tests";
import type { ResponseEvent } from "../telemetry/requests";
import type { ReactElement } from "jsx-dom";

/** How long the "Copied!" status stays visible after copying tests. */
const COPY_STATUS_MS = 2000;

export class TestPanel extends Panel {
	private tests: TestCaseEvent[] = [];
	private regressionTests: string[] = [];
	private copyStatusTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		containerId: string,
		instanceId: number,
		root: ParentNode = document,
	) {
		super(
			containerId,
			instanceId,
			"drafter-debug-tests",
			"Your Tests",
			root,
		);
	}

	protected get initialContent() {
		return (
			<>
				<div
					class={`drafter-debug-current-tests-summary drafter-debug-current-tests-summary-${this.instanceId}`}
				></div>
				<br></br>
				<strong>Details: </strong>
				<div
					class={`drafter-debug-current-tests-content-list drafter-debug-current-tests-content-list-${this.instanceId}`}
				></div>
				<div>
					<h4>Regression Tests</h4>
					<div class="drafter-debug-test-actions">
						<div>
							<p>
								Regression tests are used to verify that the
								code you've already written will continue to
								behave the same way in the future. As you
								interact with your website, Drafter
								automatically records the visits and can
								generate <em>Regression Tests</em> based on your
								actions. You can copy the tests to your Python
								code or download them as a .py file to run
								later.
							</p>
							<p>
								Note that Regression tests are not a replacement
								for unit tests, which are written by hand to
								verify specific behaviors of your code.
								Regression tests are there to help you catch
								unexpected changes in behavior as you modify
								your code. They are a good idea when you think
								your website is mostly working. These kinds of
								tests are are sometimes called "brittle" tests
								because they can break easily if you change your
								website's behavior in a way that is actually
								correct. Use them with care.
							</p>
						</div>
						<div>
							<button class="drafter-debug-copy-tests-btn drafter-debug-button--">
								Copy Regression Tests
							</button>
							<button class="drafter-debug-download-tests-btn drafter-debug-button--">
								Download Tests (.py)
							</button>
							<span
								class="drafter-debug-copy-tests-status"
								role="status"
							></span>
						</div>
					</div>
				</div>
			</>
		);
	}

	public override initialize() {
		const content = this.getContentElement();
		content
			.querySelector(".drafter-debug-copy-tests-btn")
			?.addEventListener("click", () => {
				void this.copyAllTests();
			});
		content
			.querySelector(".drafter-debug-download-tests-btn")
			?.addEventListener("click", () => this.downloadAllTests());
	}

	/**
	 * Remember the regression test the server generated for a page visit.
	 *
	 * A successful visit's `formatted_page_content` is an `assert_equal(...)`
	 * call pairing the route call with the payload it returned. Error
	 * responses and payloads without a test form (e.g. `Redirect`) carry
	 * rendered HTML or nothing there instead, so they are skipped. Repeated
	 * identical visits produce one test.
	 */
	public recordVisit(response: ResponseEvent): void {
		const test = response.formatted_page_content;
		if (
			response.has_errors ||
			!test?.startsWith("assert_equal(") ||
			this.regressionTests.includes(test)
		) {
			return;
		}
		this.regressionTests.push(test);
	}

	/**
	 * Build the regression-test source from every page visit so far: one
	 * generated `assert_equal` per distinct successful visit, ready to paste
	 * into the student's file above `start_server`.
	 */
	public buildTestSource(): string {
		const lines = [
			"# Regression tests generated by the Drafter debug panel.",
			"# Paste these into your file, above start_server(...).",
			"from drafter import *",
			"",
		];
		if (this.regressionTests.length === 0) {
			lines.push(
				"# No pages have been visited yet. Use your website, then copy again.",
			);
		}
		for (const test of this.regressionTests) {
			lines.push(test);
			lines.push("");
		}
		return lines.join("\n");
	}

	/** Forget the visit tests (the history they came from was cleared). */
	public clearRegressionTests(): void {
		this.regressionTests = [];
	}

	private async copyAllTests(): Promise<void> {
		const source = this.buildTestSource();
		let copied: boolean;
		try {
			await navigator.clipboard.writeText(source);
			copied = true;
		} catch {
			// Clipboard API unavailable (e.g. insecure context): fall back
			// to a transient textarea + execCommand copy.
			const scratch = document.createElement("textarea");
			scratch.value = source;
			document.body.appendChild(scratch);
			scratch.select();
			try {
				copied = document.execCommand("copy");
			} catch {
				copied = false;
			} finally {
				scratch.remove();
			}
		}
		this.showCopyStatus(
			copied
				? "Copied!"
				: "Could not copy; use Download Tests (.py) instead.",
		);
	}

	/** Briefly announce the copy result next to the buttons. */
	private showCopyStatus(message: string): void {
		const status = this.getContentElement().querySelector(
			".drafter-debug-copy-tests-status",
		);
		if (!status) {
			return;
		}
		status.textContent = message;
		if (this.copyStatusTimer !== null) {
			clearTimeout(this.copyStatusTimer);
		}
		this.copyStatusTimer = setTimeout(() => {
			status.textContent = "";
			this.copyStatusTimer = null;
		}, COPY_STATUS_MS);
	}

	private downloadAllTests(): void {
		const source = this.buildTestSource();
		const blob = new Blob([source], { type: "text/x-python" });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = "drafter_tests.py";
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		URL.revokeObjectURL(url);
	}

	public renderTest(testCase: TestCaseEvent): void {
		this.tests.push(testCase);
		const testList = this.queryWithin(
			this.scopedSelector("drafter-debug-current-tests-content-list"),
			"DebugPanel: Tests section not found.",
		);

		const statusIcon = testCase.passed ? "✅" : "❌";
		const statusClass = testCase.passed ? "test-passed" : "test-failed";

		const testElement = (
			<div class={`test-case ${statusClass}`}>
				<div class="test-case-header">
					<span class="test-status">{statusIcon}</span>
					<span class="test-line">Line {testCase.line}</span>
					<code class="test-caller">{testCase.caller}</code>
				</div>
				{!testCase.passed && testCase.message ? (
					<pre class="test-case-message">{testCase.message}</pre>
				) : null}
				<div class="test-case-diff"></div>
			</div>
		);

		testList.appendChild(testElement);

		this.addDiffToTestElement(testCase, testElement);
	}

	private addDiffToTestElement(
		testCase: TestCaseEvent,
		testElement: ReactElement,
	): void {
		if (!testCase.passed) {
			const diffString = testCase.diff_html;

			console.log(diffString);

			const diffContainer = testElement.querySelector(".test-case-diff");
			if (diffContainer) {
				diffContainer.innerHTML = Diff2Html.html(diffString, {
					drawFileList: false,
					matching: "words",
					diffStyle: "char",
					outputFormat: "side-by-side",
					// outputFormat: "line-by-line",
				});
			} else {
				throw new Error("DebugPanel: Diff container not found.");
			}
		}
	}

	/** Number of failing tests so far (drives the Tests tab badge). */
	public getFailCount(): number {
		return this.tests.filter((t) => !t.passed).length;
	}

	public updateTestSummary(): void {
		const summaryElement = this.queryWithin(
			this.scopedSelector("drafter-debug-current-tests-summary"),
			"DebugPanel: Test summary section not found.",
		);

		const totalTests = this.tests.length;
		const passedTests = this.tests.filter((t) => t.passed).length;
		const failedTests = totalTests - passedTests;

		summaryElement.replaceChildren(
			<div class="test-summary">
				<strong>Summary:</strong>
				<div>Total Tests: {totalTests}</div>
				<div>✅ Passed: {passedTests}</div>
				<div>❌ Failed: {failedTests}</div>
			</div>,
		);
	}
}
